import {cookieApprovalStore} from '@fl/cmsch-fe-library';
import {none, opt, Opt} from 'ts-opt';

import {Config} from './config';

interface WindowWithGTagDataLayer extends Window {
    dataLayer: Array<unknown> | undefined;
}

type GALoadFinishedHandler = (load: boolean) => void;

declare const window: WindowWithGTagDataLayer;

/** Timeout used when waiting for Google Analytics client/session ids. */
const gTagIdsTimeout = 8000;
/** DOM id used for the dynamically injected Google Analytics script tag. */
const googleAnalyticsId = 'google-analytics-script';

/** Pushes Google Analytics commands into the shared `dataLayer`. */
function gtag(..._: Array<unknown>): void {
    // dataLayer only works with implicit 'arguments' variable from function
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
}

/**
 * Loads the Google Analytics script and initializes the data layer when available.
 *
 * @param loadFinishedHandler Callback notified once loading succeeds.
 */
const initializeGTM = (loadFinishedHandler: GALoadFinishedHandler): void => {
    if (Config.gaTrackingId) {
        window.dataLayer = window.dataLayer || [];

        gtag('js', new Date());
        gtag('config', Config.gaTrackingId);
        loadFinishedHandler(true);
    }
};

/**
 * Injects the Google Analytics script element when tracking is enabled.
 *
 * @param loadFinishedHandler Callback notified after the script is loaded.
 */
const loadGoogleAnalytics = (loadFinishedHandler: GALoadFinishedHandler): void => {
    if (Config.gaTrackingId && !document.getElementById(googleAnalyticsId)) {
        const gaScript = document.createElement('script');
        gaScript.async = true;
        gaScript.id = googleAnalyticsId;
        gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${Config.gaTrackingId}`;
        document.head.appendChild(gaScript);

        gaScript.onload = initializeGTM.bind(null, loadFinishedHandler);
    }
};

/**
 * Removes the Google Analytics script element and clears the shared data layer.
 *
 * @param loadFinishedHandler Callback notified after unloading completes.
 */
const unLoadGoogleAnalytics = (loadFinishedHandler: GALoadFinishedHandler): void => {
    if (Config.gaTrackingId) {
        const gaScript = document.getElementById(googleAnalyticsId);
        if (gaScript?.parentNode) {
            gaScript.parentNode.removeChild(gaScript);
            window.dataLayer = undefined;
        }
        loadFinishedHandler(false);
    }
};

/**
 * Enables or disables Google Analytics script loading for the current session.
 *
 * @param load Whether analytics should be loaded.
 * @param loadFinishedHandler Callback notified after the load state changes.
 *
 * @example
 * handleGoogleAnalyticsScript(true, setIsLoaded);
 */
export const handleGoogleAnalyticsScript = (load: boolean, loadFinishedHandler: GALoadFinishedHandler): void => {
    if (load) {
        loadGoogleAnalytics(loadFinishedHandler);
    } else {
        unLoadGoogleAnalytics(loadFinishedHandler);
    }
};

/** Pair of Google Analytics client/session identifiers used for legacy bridge messages. */
export interface GTagClientAndSessionId {
    /** Google Analytics client id. */
    clientId: string;
    /** Google Analytics session id. */
    sessionId: string;
}

let cachedGTagClientAndSessionId: Opt<GTagClientAndSessionId> = none;

/**
 * Reads the current Google Analytics client id from the `gtag` runtime.
 *
 * @returns Promise resolving to the current client id.
 */
async function getClientId(): Promise<string> {
    return new Promise<string>(resolve => {
        gtag('get', Config.gaTrackingId, 'client_id', resolve);
    });
}

/**
 * Reads the current Google Analytics session id from the `gtag` runtime.
 *
 * @returns Promise resolving to the current session id.
 */
async function getSessionId(): Promise<string> {
    return new Promise<string>(resolve => {
        gtag('get', Config.gaTrackingId, 'session_id', resolve);
    });
}

/**
 * Loads and caches the current Google Analytics ids.
 *
 * @returns Cached identifiers when the `gtag` runtime responds successfully.
 */
async function loadGTagIds(): Promise<Opt<GTagClientAndSessionId>> {
    const [clientId, sessionId] = await Promise.all([getClientId(), getSessionId()]);

    cachedGTagClientAndSessionId = opt({clientId, sessionId});

    return cachedGTagClientAndSessionId;
}

/**
 * Rejects when Google Analytics ids are not available within the configured timeout.
 *
 * @returns Promise that rejects after {@link gTagIdsTimeout}.
 */
async function rejectAfterTimeout(): Promise<Opt<GTagClientAndSessionId>> {
    return new Promise<Opt<GTagClientAndSessionId>>((_, reject) => {
        setTimeout(
            reject,
            gTagIdsTimeout,
            new Error(`request for gTagIds were not resolved in ${gTagIdsTimeout}ms, gTag may be blocked`),
        );
    });
}

/**
 * Logs Google Analytics loading errors and returns an empty result.
 *
 * @param error Failure raised while reading Google Analytics ids.
 * @returns Empty optional value after logging the error.
 */
function handleError(error: Error): Opt<GTagClientAndSessionId> {
    // eslint-disable-next-line no-console
    console.error(error);

    return none;
}

/**
 * Returns Google Analytics client/session ids when tracking is enabled.
 *
 * @returns Optional client/session pair or `none` when analytics is disabled or unavailable.
 *
 * @example
 * const ids = await getGTagIds();
 */
export async function getGTagIds(): Promise<Opt<GTagClientAndSessionId>> {
    if (!Config.gaTrackingId || !cookieApprovalStore.isStatisticalAccepted()) return none;
    if (cachedGTagClientAndSessionId.isSome()) return cachedGTagClientAndSessionId;

    return Promise.race([loadGTagIds(), rejectAfterTimeout()])
        .catch(handleError);
}
